// The server sends what a notification is *about* (entityType + entityId); mapping
// that onto a route is the frontend's job. Purchase routes are lowercase and PMS
// routes are uppercase — react-router matches case-sensitively, so keep them exact.
export const NOTIFICATION_ROUTES = {
  PR: (id) => `/purchase_requisition/${id}`,
  RFQ: (id) => `/rfq/${id}`,
  CQ: (id) => `/qc/edit/${id}`,
  PO: (id) => `/purchase-order/edit/${id}`,
  GR: (id) => `/goods/${id}`,
  PMS: (id) => `/PMS/jobcard/${id}`,
  // Neither certificates nor defect reports have a detail route, so both land on
  // their list. DefectClosed points here (the report); DefectReported points at
  // PMS (the job) — two documents describing the same defect.
  PMSCERT: () => `/PMS/certificate`,
  DEFECT: () => `/PMS/dashboard/defect`,
  // An item id is unique only within its own table, so stock rows name the
  // namespace in entityType. A bare STOCK is deliberately absent from this map:
  // it means the tenant's item category matched none of the four, so it falls
  // through to the fallback page rather than being guessed at a namespace.
  STOCKPART: (id) => `/inventory/consumption_part/${id}`,
  STOCKGENERAL: (id) => `/inventory/consumption_general/${id}`,
  STOCKSTORE: (id) => `/inventory/consumption_store/${id}`,
  STOCKOIL: (id) => `/inventory/consumption_oil/${id}`,
  // StockLowDigest's entityId is a vessel id, not an item, and there is no
  // per-vessel low-stock view — so it lands on the inventory dashboard, which
  // carries the low-stock overview. Better than the fallback page for something
  // that fires daily.
  STOCKDIGEST: () => `/inventory`,
};

// Where a row lands when it names an entityType this build has no route for yet.
// A new document type ships in the feed before the route map knows it; sending
// the user to a real "not here" page beats a dead click.
export const NOTIFICATION_UNAVAILABLE_ROUTE = "/notifications/unavailable";

// Three outcomes:
//   - a mapped entityType with an id → its route
//   - an *unmapped* entityType → the fallback page (a type is shipping ahead of
//     this route map; it still meant to open something)
//   - no entityType, or a mapped type that arrived without an id → unclickable.
//     entityType and entityId always come as a pair from BE, so the id-less case
//     is not a real state — but MaintenanceDeleted, the budget events and legacy
//     scheduler rows carry no entityType at all, and those stay dead links.
export const targetOf = (notification) => {
  const route = NOTIFICATION_ROUTES[notification?.entityType];

  if (route && notification?.entityId != null) {
    return route(notification.entityId);
  }

  if (notification?.link) return notification.link;

  if (notification?.entityType && !route) return NOTIFICATION_UNAVAILABLE_ROUTE;

  return null;
};

export const normalizeNotification = (notification = {}) => ({
  ...notification,
  id: Number(notification.id),
  metadata: notification.metadata || {},
});

// createdAtUtc arrives naive ("2026-08-26T08:18:57.19") and is UTC, so it needs the
// Z appended or the browser reads it as local time.
export const parseNotificationDate = (value) => {
  if (!value) return null;

  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasZone ? value : `${value}Z`);
};

const byNewestFirst = (a, b) =>
  (parseNotificationDate(b.createdAtUtc)?.getTime() || 0) -
  (parseNotificationDate(a.createdAtUtc)?.getTime() || 0);

// A push and a refetch delivering the same row is normal, not a race to defend
// against — id is stable and unique per recipient, so dedupe on it and let the
// incoming copy win.
export const mergeNotifications = (existing = [], incoming = []) => {
  const byId = new Map(existing.map((row) => [row.id, row]));

  incoming.forEach((row) => {
    const normalized = normalizeNotification(row);
    byId.set(normalized.id, normalized);
  });

  return [...byId.values()].sort(byNewestFirst);
};

export const isFeatureDisabled = (envelope) =>
  envelope?.messageCode === "FEATURE_DISABLED";

// An empty bell, not an error.
export const isEmptyFeed = (envelope) => envelope?.messageCode === "204";
